
import React, { useState, useMemo, useEffect } from 'react'
import {
  Search,
  Users,
  User,
  Mail,
  Phone,
  Building2,
  ChevronDown,
  ChevronRight,
  Filter,
  Download,
  Maximize2,
  Minimize2,
  UserCircle
} from 'lucide-react'
import { useSalaryAccess } from '../../hooks/useSalaryAccess'
import {
  getAllMonumentaEmployees,
  MonumentaEmployee,
  getDepartmentHierarchyConfig,
  getLeadershipKeywords,
  formatPersonName,
  caseInsensitiveEquals,
  // NEW: Complete hierarchy system
  getHierarchyNodeWithChildren,
  loadOrganizationalHierarchy,
  getHierarchyStatistics,
  type OrganizationalHierarchyNode
} from '@/lib/real-data-service'
// Import workforce API - only management tree endpoint
import {
  getMyManagementTree
} from '@/api/workforce'
import { usePageTitle } from '@/stores/page-title-store'

interface OrgEmployee {
  id: string
  name: string
  position: string
  department: string
  email: string
  phone: string
  avatar?: string
  managerId?: string
  level: number
  directReports: OrgEmployee[]
  realEmployee?: MonumentaEmployee
}

// Convert OrganizationalHierarchyNode to OrgEmployee format for UI compatibility
const convertHierarchyToOrgEmployee = (node: OrganizationalHierarchyNode, allEmployees: MonumentaEmployee[], level: number = 0, managerId?: string): OrgEmployee => {
  const orgEmployee: OrgEmployee = {
    id: node.id,
    name: node.name,
    position: node.role,
    department: node.department,
    email: node.email,
    phone: `+55 11 99999-${node?.id?.replace(/[^0-9]/g, '').padStart(4, '0')}`, // Generate phone from ID
    level,
    directReports: [],
    managerId,
    // Find the real employee data for additional info
    realEmployee: allEmployees.find(emp =>
      caseInsensitiveEquals(emp.email, node.email) ||
      caseInsensitiveEquals(emp.name, node.name)
    )
  };

  // Convert direct reports recursively
  node.directReports.forEach(child => {
    const childOrgEmployee = convertHierarchyToOrgEmployee(child, allEmployees, level + 1, orgEmployee.id);
    orgEmployee.directReports.push(childOrgEmployee);
  });

  return orgEmployee;
};

// Build organizational hierarchy using the new complete hierarchy system
const buildNewOrgHierarchy = async (allEmployees: MonumentaEmployee[]): Promise<OrgEmployee> => {

  // Load the complete hierarchy from the new system
  const hierarchyRoot = await loadOrganizationalHierarchy();

  if (!hierarchyRoot) {
    throw new Error('Failed to load organizational hierarchy');
  }


  // Convert to OrgEmployee format
  const orgRoot = convertHierarchyToOrgEmployee(hierarchyRoot, allEmployees, 0);

  // Get hierarchy statistics
  const stats = await getHierarchyStatistics();

  return orgRoot;
};

// NEW: Build organizational hierarchy using management tree API (no rules)
const buildScopedOrgHierarchy = async (): Promise<OrgEmployee> => {

  try {
    const managementTree = await getMyManagementTree({
      includeManager: true
    });

    return buildHierarchyFromManagementTree(managementTree);
  } catch (error) {
    console.error('❌ [MANAGEMENT TREE] Failed to get management tree:', error);
    throw new Error('Failed to load organizational hierarchy');
  }
};


// Build hierarchy from management tree (no rules)
// New API returns nested EmployeeTree structure with reports array
const buildHierarchyFromManagementTree = (employeeTree: any, level: number = 0): OrgEmployee => {
  
  // Count total reports recursively
  const countReports = (node: any): number => {
    let count = node.reports?.length || 0;
    node.reports?.forEach((report: any) => {
      count += countReports(report);
    });
    return count;
  };
  
  const totalReports = countReports(employeeTree);

  // Convert EmployeeTree to OrgEmployee recursively
  const convertToOrgEmployee = (empTree: any, currentLevel: number): OrgEmployee => {
    const orgEmp: OrgEmployee = {
      id: empTree.employeeId || 'unknown',
      name: empTree.personalInfo?.name || empTree.name || 'Unknown',
      position: empTree.employment?.role?.name || empTree.position || 'Team Member',
      department: empTree.employment?.department?.name || empTree.department || 'Unknown',
      email: empTree.email || '',
      phone: empTree.personalInfo?.phone || `+55 11 99999-${Math.random().toString().slice(-4)}`,
      level: currentLevel,
      directReports: []
    };

    // Recursively convert direct reports
    if (empTree.reports && Array.isArray(empTree.reports)) {
      orgEmp.directReports = empTree.reports.map((report: any) => 
        convertToOrgEmployee(report, currentLevel + 1)
      );
    }

    return orgEmp;
  };

  const result = convertToOrgEmployee(employeeTree, level);
  
  return result;
};


// Department colors using real department data and Monumenta brand palette
const departmentColors = Object.keys(getDepartmentHierarchyConfig()).reduce((colors, dept) => {
  const departmentHierarchy = getDepartmentHierarchyConfig();
  const hierarchy = departmentHierarchy[dept as keyof typeof departmentHierarchy];
  const color = hierarchy.color;
  
  // Convert hex to Tailwind-like classes (simplified approach)
  const getColorClasses = (hexColor: string) => {
    switch(hexColor) {
      case '#D90A2C': return 'bg-red-500 bg-opacity-20 border-red-500 border-opacity-40 text-red-500';
      case '#4760E0': return 'bg-blue-500 bg-opacity-20 border-blue-500 border-opacity-40 text-blue-500';
      case '#10B981': return 'bg-green-500 bg-opacity-20 border-green-500 border-opacity-40 text-green-500';
      case '#6366F1': return 'bg-indigo-500 bg-opacity-20 border-indigo-500 border-opacity-40 text-indigo-500';
      case '#8B5CF6': return 'bg-purple-500 bg-opacity-20 border-purple-500 border-opacity-40 text-purple-500';
      case '#EC4899': return 'bg-pink-500 bg-opacity-20 border-pink-500 border-opacity-40 text-pink-500';
      case '#F59E0B': return 'bg-yellow-500 bg-opacity-20 border-yellow-500 border-opacity-40 text-yellow-500';
      case '#EF4444': return 'bg-red-600 bg-opacity-20 border-red-600 border-opacity-40 text-red-600';
      case '#06B6D4': return 'bg-cyan-500 bg-opacity-20 border-cyan-500 border-opacity-40 text-cyan-500';
      case '#84CC16': return 'bg-lime-500 bg-opacity-20 border-lime-500 border-opacity-40 text-lime-500';
      case '#F97316': return 'bg-orange-500 bg-opacity-20 border-orange-500 border-opacity-40 text-orange-500';
      case '#A855F7': return 'bg-violet-500 bg-opacity-20 border-violet-500 border-opacity-40 text-violet-500';
      case '#14B8A6': return 'bg-teal-500 bg-opacity-20 border-teal-500 border-opacity-40 text-teal-500';
      case '#F43F5E': return 'bg-rose-500 bg-opacity-20 border-rose-500 border-opacity-40 text-rose-500';
      default: return 'bg-gray-500 bg-opacity-20 border-gray-500 border-opacity-40 text-gray-500';
    }
  };
  
  colors[dept] = getColorClasses(color);
  return colors;
}, {} as Record<string, string>);

// Add fallback colors for departments not in hierarchy
const additionalDepartmentColors = {
  'Default': 'bg-brand-gray bg-opacity-20 border-brand-gray border-opacity-40 text-brand-gray',
};

const allDepartmentColors: Record<string, string> = { ...departmentColors, ...additionalDepartmentColors };

interface EmployeeNodeProps {
  employee: OrgEmployee
  expanded: boolean
  onToggle: (id: string) => void
  onEmployeeClick: (employee: OrgEmployee) => void
  searchTerm: string
  isHighlighted: boolean
}

function EmployeeNode({ employee, expanded, onToggle, onEmployeeClick, searchTerm, isHighlighted }: EmployeeNodeProps) {
  const hasDirectReports = employee.directReports.length > 0
  const departmentColor = allDepartmentColors[employee.department] || allDepartmentColors['Default']
  
  const isSearchMatch = searchTerm && (
    employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.department.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="relative">
      {/* Employee Card */}
      <div className={`
        relative card transition-all duration-300 cursor-pointer group
        ${isHighlighted || isSearchMatch ? 'border-brand-primary shadow-monumenta-brand' : 'hover:border-brand-primary hover:shadow-monumenta-brand'}
        ${employee.level === 0 ? 'border-brand-primary shadow-monumenta-brand' : ''}
      `}>
        <div 
          onClick={() => onEmployeeClick(employee)}
          className="p-4"
        >
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center shadow-monumenta-brand group-hover:shadow-monumenta-brand-lg transition-all duration-300">
              <UserCircle className="w-8 h-8 text-brand-dark" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-semibold text-white truncate group-hover:text-brand-primary transition-colors duration-200">{employee.name}</h3>
              <p className="text-sm text-brand-gray truncate font-body">{employee.position}</p>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium font-display ${departmentColor} mt-1`}>
                {employee.department}
              </span>
            </div>
            {hasDirectReports && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onToggle(employee.id)
                }}
                className="btn-icon hover:bg-brand-primary/10 text-brand-gray hover:text-brand-primary"
              >
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            )}
          </div>
        </div>

        {/* Direct Reports Count Badge */}
        {hasDirectReports && (
          <div className="absolute -top-2 -right-2 bg-brand-primary text-brand-dark text-xs rounded-full w-6 h-6 flex items-center justify-center font-display font-medium shadow-monumenta-brand animate-pulse">
            {employee.directReports.length}
          </div>
        )}
      </div>

      {/* Direct Reports */}
      {hasDirectReports && expanded && (
        <div className="ml-8 mt-4 space-y-4">
          {/* Connection Line */}
          <div className="absolute left-6 top-16 bottom-0 w-px bg-brand-gray bg-opacity-40"></div>
          
          {employee.directReports.map((report, index) => (
            <div key={report.id} className="relative">
              {/* Horizontal connection */}
              <div className="absolute -left-8 top-6 w-8 h-px bg-brand-gray bg-opacity-40"></div>
              {/* Vertical connection for non-last items */}
              {index < employee.directReports.length - 1 && (
                <div className="absolute -left-8 top-6 bottom-0 w-px bg-brand-gray bg-opacity-40"></div>
              )}
              
              <EmployeeNode
                employee={report}
                expanded={expanded}
                onToggle={onToggle}
                onEmployeeClick={onEmployeeClick}
                searchTerm={searchTerm}
                isHighlighted={isHighlighted}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function OrgChartPage() {
  const { canViewSalaries } = useSalaryAccess()
  usePageTitle('Organograma')
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['1']))
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedEmployee, setSelectedEmployee] = useState<OrgEmployee | null>(null)
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [orgData, setOrgData] = useState<OrgEmployee | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load organizational data using management tree API (no rules)
  useEffect(() => {
    const loadOrgData = async () => {
      try {
        setIsLoading(true)
        setError(null)


        // Build organizational hierarchy from management tree
        const hierarchyData = await buildScopedOrgHierarchy();

        setOrgData(hierarchyData)

      } catch (err) {
        console.error('❌ [ORGCHART] Error loading management tree:', err);
        setError(err instanceof Error ? err.message : 'Failed to load organizational data');
      } finally {
        setIsLoading(false)
      }
    }

    loadOrgData()
  }, [])

  // Flatten org structure for search and statistics
  const flatEmployees = useMemo(() => {
    if (!orgData) return []

    const flatten = (employee: OrgEmployee): OrgEmployee[] => {
      return [employee, ...employee.directReports.flatMap(flatten)]
    }
    return flatten(orgData)
  }, [orgData])

  // Department statistics
  const departmentStats = useMemo(() => {
    const stats: Record<string, number> = {}
    flatEmployees.forEach(emp => {
      stats[emp.department] = (stats[emp.department] || 0) + 1
    })
    return stats
  }, [flatEmployees])

  const filteredEmployees = useMemo(() => {
    if (!searchTerm && selectedDepartment === 'all') return flatEmployees

    return flatEmployees.filter(emp => {
      const matchesSearch = !searchTerm ||
        emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.department.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesDepartment = selectedDepartment === 'all' || emp.department === selectedDepartment

      return matchesSearch && matchesDepartment
    })
  }, [flatEmployees, searchTerm, selectedDepartment])

  // Add loading state
  if (isLoading) {
    return (
      
        <div className="space-y-8 animate-fade-in-up">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-brand-gray-light font-body">Carregando organograma...</p>
            </div>
          </div>
        </div>
      
    )
  }

  // Add error state
  if (error) {
    return (
      
        <div className="space-y-8 animate-fade-in-up">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="text-red-400 mb-4">Erro ao carregar organograma</div>
              <p className="text-brand-gray-light font-body text-sm">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 px-4 py-2 bg-brand-primary text-white rounded-monumenta text-sm hover:bg-brand-primary/80 transition-colors"
              >
                Tentar Novamente
              </button>
            </div>
          </div>
        </div>
      
    )
  }

  if (!orgData) {
    return (
      
        <div className="space-y-8 animate-fade-in-up">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <p className="text-brand-gray-light font-body">Nenhum dado encontrado</p>
            </div>
          </div>
        </div>
      
    )
  }

  const handleToggleNode = (id: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleExpandAll = () => {
    const allIds = new Set(flatEmployees.map(emp => emp.id))
    setExpandedNodes(allIds)
  }

  const handleCollapseAll = () => {
    setExpandedNodes(new Set(['1'])) // Keep only root expanded
  }

  const handleEmployeeClick = (employee: OrgEmployee) => {
    setSelectedEmployee(employee)
  }

  return (
    <>
      <div className={`space-y-8 ${isFullscreen ? 'fixed inset-0 bg-brand-dark z-50 p-6 overflow-auto' : ''}`}>
        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="btn-secondary flex items-center gap-2 group"
          >
            {isFullscreen ? <Minimize2 size={16} className="transition-transform duration-200 group-hover:scale-110" /> : <Maximize2 size={16} className="transition-transform duration-200 group-hover:scale-110" />}
            {isFullscreen ? 'Sair da Tela Cheia' : 'Tela Cheia'}
          </button>
          <button className="btn-primary flex items-center gap-2 group">
            <Download size={16} className="transition-transform duration-200 group-hover:scale-110" />
            Exportar
          </button>
        </div>


        {/* Enhanced Stats Cards with New Hierarchy System */}
        <div className="dashboard-grid lg:grid-cols-6">
          <div className="metric-card">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-brand-primary" size={20} />
            </div>
            <div className="metric-value">{flatEmployees.length}</div>
            <div className="metric-label">Total de Pessoas</div>
          </div>
          
          {Object.entries(departmentStats).slice(0, 5).map(([dept, count]) => (
            <div key={dept} className="metric-card">
              <div className="flex items-center justify-between mb-2">
                <Building2 className="text-brand-primary" size={20} />
              </div>
              <div className="metric-value">{count}</div>
              <div className="metric-label">{dept}</div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="card-hover">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-brand-primary transition-colors duration-200 pointer-events-none z-10" size={16} />
                <input
                  type="text"
                  placeholder="Buscar por nome, cargo ou departamento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10 pr-4 py-3 w-80"
                  style={{ paddingLeft: '3rem' }}
                />
              </div>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="form-select py-3"
              >
                <option value="all">Todos os Departamentos</option>
                {Object.keys(departmentStats).map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleExpandAll}
                className="btn-ghost text-sm"
              >
                Expandir Todos
              </button>
              <button
                onClick={handleCollapseAll}
                className="btn-ghost text-sm"
              >
                Recolher Todos
              </button>
            </div>
          </div>
        </div>

        {/* Organizational Chart */}
        <div className="card-hover animate-fade-in-up">
          <div className="overflow-auto" style={{ minHeight: '600px' }}>
            <EmployeeNode
              employee={orgData}
              expanded={expandedNodes.has(orgData.id)}
              onToggle={handleToggleNode}
              onEmployeeClick={handleEmployeeClick}
              searchTerm={searchTerm}
              isHighlighted={false}
            />
          </div>
        </div>

        {/* Search Results */}
        {searchTerm && (
          <div className="bg-brand-dark-light rounded border border-brand-gray border-opacity-20 p-6">
            <h3 className="font-display text-lg font-semibold text-white mb-4">
              Resultados da Busca ({filteredEmployees.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEmployees.map(employee => (
                <div
                  key={employee.id}
                  onClick={() => handleEmployeeClick(employee)}
                  className="p-4 border border-brand-gray border-opacity-20 rounded hover:border-brand-primary hover:border-opacity-40 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-brand-primary bg-opacity-20 rounded-full flex items-center justify-center">
                      <UserCircle className="w-6 h-6 text-brand-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-display font-medium text-white truncate">{employee.name}</h4>
                      <p className="text-sm text-brand-gray truncate">{employee.position}</p>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${allDepartmentColors[employee.department] || allDepartmentColors['Default']} mt-1`}>
                        {employee.department}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-brand-dark-light rounded max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-brand-gray border-opacity-20">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-display text-2xl font-bold text-white">Perfil do Colaborador</h2>
                <button
                  onClick={() => setSelectedEmployee(null)}
                  className="text-brand-gray hover:text-white transition-colors duration-300"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Employee Info */}
                <div className="flex items-center space-x-6">
                  <div className="w-20 h-20 bg-brand-primary bg-opacity-20 rounded-full flex items-center justify-center">
                    <UserCircle className="w-12 h-12 text-brand-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold text-white">{selectedEmployee.name}</h3>
                    <p className="text-lg text-brand-gray">{selectedEmployee.position}</p>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${allDepartmentColors[selectedEmployee.department] || allDepartmentColors['Default']} mt-2`}>
                      {selectedEmployee.department}
                    </span>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-brand-gray" />
                    <div>
                      <p className="text-sm text-brand-gray">Email</p>
                      <p className="font-medium text-white">{selectedEmployee.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-brand-gray" />
                    <div>
                      <p className="text-sm text-brand-gray">Telefone</p>
                      <p className="font-medium text-white">{selectedEmployee.phone}</p>
                    </div>
                  </div>
                </div>

                {/* Hierarchy Info */}
                <div>
                  <h4 className="font-display font-semibold text-white mb-2">Informações Hierárquicas</h4>
                  <div className="space-y-2">
                    <p><span className="text-brand-gray">Nível:</span> <span className="text-white">{selectedEmployee.level + 1}</span></p>
                    <p><span className="text-brand-gray">Subordinados Diretos:</span> <span className="text-white">{selectedEmployee.directReports.length}</span></p>
                    {selectedEmployee.managerId && (
                      <p><span className="text-brand-gray">Reporta para:</span> <span className="text-white">Gestor Direto</span></p>
                    )}
                  </div>
                </div>

                {/* Real Employee Data */}
                {selectedEmployee.realEmployee && (
                  <div>
                    <h4 className="font-display font-semibold text-white mb-2">Informações Profissionais</h4>
                    <div className="space-y-2">
                      <p><span className="text-brand-gray">Tipo de Contrato:</span> <span className="text-white">{selectedEmployee.realEmployee.contractType}</span></p>
                      <p><span className="text-brand-gray">Cliente:</span> <span className="text-white">{selectedEmployee.realEmployee.client}</span></p>
                      <p><span className="text-brand-gray">Performance:</span> <span className="text-white">{selectedEmployee.realEmployee.performance || 'Não avaliado'}</span></p>
                      <p><span className="text-brand-gray">Data de Admissão:</span> <span className="text-white">{new Date(selectedEmployee.realEmployee.admissionDate).toLocaleDateString('pt-BR')}</span></p>
                      <p><span className="text-brand-gray">Saldo de Férias:</span> <span className="text-white">{selectedEmployee.realEmployee.vacationBalance} dias</span></p>
                      {canViewSalaries && selectedEmployee.realEmployee.salary > 0 && (
                        <p><span className="text-brand-gray">Salário:</span> <span className="text-white">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedEmployee.realEmployee.salary)}</span></p>
                      )}
                    </div>
                  </div>
                )}

                {/* Direct Reports */}
                {selectedEmployee.directReports.length > 0 && (
                  <div>
                    <h4 className="font-display font-semibold text-white mb-3">Subordinados Diretos</h4>
                    <div className="space-y-2">
                      {selectedEmployee.directReports.map(report => (
                        <div key={report.id} className="flex items-center space-x-3 p-2 bg-brand-dark bg-opacity-50 rounded border border-brand-gray border-opacity-20">
                          <div className="w-8 h-8 bg-brand-primary bg-opacity-20 rounded-full flex items-center justify-center">
                            <UserCircle className="w-5 h-5 text-brand-primary" />
                          </div>
                          <div>
                            <p className="font-display font-medium text-white">{report.name}</p>
                            <p className="text-sm text-brand-gray">{report.position}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
} 